import { qubitRange, metricWeights } from './config.js';
import { QuantumNoiseGenerator } from './noiseGenerator.js';
import { ZoneCalculator } from './zoneCalculator.js';
import { BonusCalculator } from './bonusCalculator.js';
import quantumEngine from '../../engine/QuantumEngine.js';
import PlayerService from '../../../services/PlayerService.js';
import { getCookie } from '../../../utils/CookieManager.js';

/**
 * Simplified system for harvesting qubits based on quantum analysis
 */
export class QubitHarvestingSystem {
  /**
   * Calculate and harvest qubits based on quantum analysis data
   * @param {Object} analysisData Complete quantum analysis data
   * @returns {Promise<Object>} Harvesting results
   */
  async harvestQubits(analysisData) {
    if (!this.validateAnalysisData(analysisData)) {
      console.warn('Invalid analysis data provided for harvesting');
      return this.getMinimumHarvest();
    }

    try {
      // 1. Calculate base qubits from core metrics
      const baseQubits = this.calculateBaseQubits(analysisData);


      // 2. Calculate multipliers (simplified)
      const multiplierInfo = BonusCalculator.calculateMultipliers(analysisData);


      // 3. Calculate zones for distribution
      const zones = ZoneCalculator.calculateZones(analysisData);
      
      // 4. Add quantum noise for variety
      const noiseParams = {
        coherenceVar: parseFloat(analysisData.quantumState.coherence) / 100,
        entanglementVar: parseFloat(analysisData.quantumState.entanglement) / 100,
        resonanceVar: parseFloat(analysisData.quantumState.resonance) / 100,
        qualityFactor: parseFloat(analysisData.signalSummary.quality) / 100
      };
      const noiseMultiplier = QuantumNoiseGenerator.generate(noiseParams);

      // 5. Calculate final qubits
      const finalQubits = this.calculateFinalQubits(
        baseQubits,
        multiplierInfo.total,
        zones,
        noiseMultiplier
      );

      // 6. Process the harvest
      await this.processHarvest(finalQubits);

      // 7. Return results
      return {
        qubits: finalQubits,
        bonuses: multiplierInfo.bonuses,
        totalMultiplier: multiplierInfo.total.toFixed(2),
        totalHarvested: quantumEngine.getHarvestedQubits()
      };
    } catch (error) {
      console.error('Error during qubit harvesting:', error);
      return this.getMinimumHarvest();
    }
  }

  /**
   * Validate incoming analysis data
   * @private
   */
  validateAnalysisData(data) {
    return data?.signalSummary && 
           data?.quantumState && 
           data?.rewards;
  }

  /**
   * Calculate base qubits from core metrics
   * @private
   */
  calculateBaseQubits(data) {
    // Use core metrics with weighted importance
    const metrics = [
      data.signalSummary.quality * metricWeights.coherence,
      parseFloat(data.quantumState.coherence) * metricWeights.coherence,
      parseFloat(data.quantumState.entanglement) * metricWeights.entanglement,
      parseFloat(data.quantumState.resonance) * metricWeights.resonance
    ].filter(x => !isNaN(x) && isFinite(x));

    // Calculate weighted average
    return Math.floor(
      metrics.reduce((sum, val) => sum + Math.min(val, 100), 0) /
      (metrics.length || 1)
    );
  }

  /**
   * Calculate final qubit amount
   * @private
   */
  calculateFinalQubits(baseQubits, totalMultiplier, zones, noiseMultiplier) {
    // Normalize base value
    const normalizedBase = baseQubits / 100;
    
    // Apply multiplier
    const baseValue = normalizedBase * totalMultiplier;
    
    // Get zone position
    const zoneInfo = ZoneCalculator.calculateZonePosition(baseValue, zones);
    
    // Calculate final amount with noise
    const finalQubits = Math.floor(
      zoneInfo.min + (zoneInfo.range * zoneInfo.position * noiseMultiplier)
    );

    // Ensure within valid range
    return ZoneCalculator.validateQubitValue(finalQubits);
  }

  /**
   * Process the harvest through quantum engine and database
   * @private
   */
  async processHarvest(qubitAmount) {
    // Increment quantum engine
    for (let i = 0; i < qubitAmount; i++) {
      quantumEngine.harvestQubit();
    }

    // Update database
    try {
      const profile = getCookie('linkedProfile');
      if (!profile) {
        throw new Error('No linked profile');
      }
      if (!profile?.playerId) {
        throw new Error('Invalid profile data');
      }

      const updateResult = await PlayerService.updatePlayerData(
        profile.playerId,
        { qubits: qubitAmount },
        { increment: true }
      );

      if (!updateResult) {
        throw new Error('Database update failed');
      }
    } catch (error) {
      console.error('Error updating database:', error);
      throw error;
    }
  }

  /**
   * Get minimum harvest result for error cases
   * @private
   */
  async getMinimumHarvest() {
    const minQubits = qubitRange.min;
    quantumEngine.harvestQubit();
    
    try {
      const profile = getCookie('linkedProfile');
      if (profile?.playerId) {
        await PlayerService.updatePlayerData(
          profile.playerId,
          { qubits: minQubits },
          { increment: true }
        );
      }
    } catch (error) {
      console.error('Error in minimum harvest:', error);
    }

    return {
      qubits: minQubits,
      bonuses: [],
      totalMultiplier: '1.00',
      totalHarvested: quantumEngine.getHarvestedQubits()
    };
  }
}

export default QubitHarvestingSystem;